import { createAsyncThunk } from "@reduxjs/toolkit";
import type { ApiCarrierListItem, BlacklistCarriersResponse, CarriersSearchResponse } from "api-client";
import { rejectMessage } from "api-client";
import { OPERATOR_SCOPED_ID } from "../../constants/operator.constants";
import {
  carriersService,
  inviteCarrierOperator,
  INVITE_CARRIER_FAILED_MESSAGE,
  type InviteCarrierFormRequest,
} from "../../services/carriers.service";
import type { CarriersListState } from "./carriers.models";
import { setCarriersListShowOnlyActive } from "./carriers.slice";

type RootSlice = { carriersList: CarriersListState };

function joinCsv(values: string[]): string {
  const parts = values.map((s) => String(s).trim()).filter(Boolean);
  return parts.length ? [...new Set(parts)].join(",") : "";
}

export const fetchCarriersList = createAsyncThunk<
  CarriersSearchResponse,
  void,
  { state: RootSlice; rejectValue: string }
>("carriersList/fetch", async (_, { getState, rejectWithValue }) => {
  const q = getState().carriersList;
  try {
    const locationIds = joinCsv([...q.toolbarLocationFilterIds, ...q.listFilterLocationIds]);
    const res = await carriersService.searchCarriers({
      searchByName: q.search.trim(),
      filters: {
        operatorIds: joinCsv(q.selectedOperatorIds) || String(OPERATOR_SCOPED_ID),
        customerIds: joinCsv(q.selectedCustomerIds),
        dotStatus: joinCsv(q.listFilterDotStatus),
        riskLevel: joinCsv(q.listFilterRiskLevel),
        aviroStatus: joinCsv(q.listFilterAviroStatus),
        locationIds,
        activeOnly: q.showOnlyActive,
      },
      paging: {
        fromIndex: q.fromIndex,
        toIndex: q.toIndex,
        sortingOrder: q.sortingOrder,
        sortingField: q.sortingField,
      },
    });
    return res;
  } catch (e: unknown) {
    return rejectWithValue(rejectMessage(e, "Failed to fetch carriers"));
  }
});

export const blacklistCarriers = createAsyncThunk<
  BlacklistCarriersResponse,
  { carrierIds: number[]; action?: "blacklist" | "unblacklist" },
  { state: RootSlice; rejectValue: string }
>("carriersList/blacklist", async ({ carrierIds, action = "blacklist" }, { dispatch, rejectWithValue }) => {
  const ids = carrierIds.filter((n) => Number.isFinite(n) && n > 0);
  if (!ids.length) {
    return rejectWithValue("No carriers selected");
  }
  const resolvedAction = action === "unblacklist" ? "unblacklist" : "blacklist";
  try {
    const res = await carriersService.blacklistCarriers([OPERATOR_SCOPED_ID], {
      carrierIds: ids,
      action: resolvedAction,
    });
    try {
      await dispatch(fetchCarriersList()).unwrap();
    } catch (e: unknown) {
      return rejectWithValue(rejectMessage(e, "Carriers updated but list refresh failed"));
    }
    return res;
  } catch (e: unknown) {
    return rejectWithValue(
      rejectMessage(
        e,
        resolvedAction === "unblacklist" ? "Failed to unblacklist carriers from yard" : "Failed to blacklist carriers",
      ),
    );
  }
});

export const inviteCarrier = createAsyncThunk<
  { listItem: ApiCarrierListItem },
  InviteCarrierFormRequest,
  { state: RootSlice; rejectValue: string }
>("carriersList/inviteCarrier", async (body, { dispatch, rejectWithValue }) => {
  try {
    const res = await inviteCarrierOperator(body);
    dispatch(setCarriersListShowOnlyActive(false));
    try {
      await dispatch(fetchCarriersList()).unwrap();
    } catch (e: unknown) {
      return rejectWithValue(
        rejectMessage(e, "Carrier invited but list refresh failed"),
      );
    }
    return { listItem: res.listItem };
  } catch (e: unknown) {
    return rejectWithValue(rejectMessage(e, INVITE_CARRIER_FAILED_MESSAGE));
  }
});
